// Layer 2 (Engine): session-local claims mutations — rulings, version accept,
// tag-map confirm. Seed data stays frozen; resolution is derived.

import type { ClaimSubject, ResolvedVersion, Ruling } from '@/versions/v1/schema/claim';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import {
  AUTHORITY,
  CLAIMS,
  claimsForSession,
  MODEL_VERSIONS,
  RULINGS,
} from '@/versions/v1/shared/bunker-hill-claims';
import { resolve, diffVersions, type Resolution } from '@/versions/v1/shared/claims';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

export type ClaimsSessionState = {
  rulings: Ruling[];
  acceptedVersions: ResolvedVersion[];
  confirmedTags: ReadonlySet<string>;
};

const SEED_CONFIRMED_TAGS = new Set(
  TAG_MAPPING.filter((row) => row.seriesId)
    .slice(0, 9)
    .map((row) => `tag-${row.role}`),
);

export const INITIAL_CLAIMS_STATE: ClaimsSessionState = {
  rulings: [],
  acceptedVersions: [],
  confirmedTags: SEED_CONFIRMED_TAGS,
};

export function rule(
  state: ClaimsSessionState,
  subject: ClaimSubject,
  acceptsClaimId: string,
  reason: string,
  author: { role: PlantRoleId; name?: string },
  at: string,
): ClaimsSessionState {
  const id = `rul-session-${state.rulings.length + 1}`;
  const ruling: Ruling = {
    id,
    subject,
    acceptsClaimId,
    author,
    decidedAt: at,
    reason,
  };
  return { ...state, rulings: [...state.rulings, ruling] };
}

export function acceptVersion(
  state: ClaimsSessionState,
  at: string,
  by: { role: PlantRoleId; name?: string },
  revisedBalance = false,
): ClaimsSessionState {
  const res = resolution(state, revisedBalance);
  if (res.disputed.size > 0) return state;
  const current = currentVersion(state);
  const nextN = (current?.n ?? MODEL_VERSIONS[MODEL_VERSIONS.length - 1].n) + 1;
  const version: ResolvedVersion = {
    id: `pm-v${nextN}`,
    scope: 'plant-model',
    n: nextN,
    acceptedAt: at,
    acceptedBy: by,
    claimIds: [...res.winners.values()].map((c) => c.id),
    supersedes: current?.id,
    note: 'Accepted in session',
  };
  return { ...state, acceptedVersions: [...state.acceptedVersions, version] };
}

export function setTagConfirmed(
  state: ClaimsSessionState,
  tagId: string,
  confirmed: boolean,
): ClaimsSessionState {
  const has = state.confirmedTags.has(tagId);
  if (confirmed === has) return state;
  const confirmedTags = new Set(state.confirmedTags);
  if (confirmed) confirmedTags.add(tagId);
  else confirmedTags.delete(tagId);
  return { ...state, confirmedTags };
}

export function confirmTag(state: ClaimsSessionState, tagId: string): ClaimsSessionState {
  return setTagConfirmed(state, tagId, true);
}

export function unconfirmTag(state: ClaimsSessionState, tagId: string): ClaimsSessionState {
  return setTagConfirmed(state, tagId, false);
}

export function confirmAllTags(
  state: ClaimsSessionState,
  tagIds: readonly string[],
): ClaimsSessionState {
  let next = state;
  for (const id of tagIds) {
    next = confirmTag(next, id);
  }
  return next;
}

export function resolution(
  state: ClaimsSessionState,
  revisedBalance = false,
): Resolution {
  const claims = claimsForSession(revisedBalance);
  const rulings = [...RULINGS, ...state.rulings];
  return resolve(claims, rulings, AUTHORITY);
}

export function currentVersion(state: ClaimsSessionState): ResolvedVersion {
  if (state.acceptedVersions.length > 0) {
    return state.acceptedVersions[state.acceptedVersions.length - 1];
  }
  return MODEL_VERSIONS[MODEL_VERSIONS.length - 1];
}

export function previousVersion(state: ClaimsSessionState): ResolvedVersion {
  const current = currentVersion(state);
  const seeded = MODEL_VERSIONS.find((v) => v.id === current.supersedes);
  if (seeded) return seeded;
  const priorSession = state.acceptedVersions[state.acceptedVersions.length - 2];
  if (priorSession) return priorSession;
  return MODEL_VERSIONS[0];
}

export function pendingAcceptDiff(
  state: ClaimsSessionState,
  revisedBalance = false,
) {
  const res = resolution(state, revisedBalance);
  const current = currentVersion(state);
  const claims = claimsForSession(revisedBalance);
  const nextClaimIds = [...res.winners.values()].map((c) => c.id);
  const nextVersion: ResolvedVersion = {
    id: 'pending',
    scope: 'plant-model',
    n: current.n + 1,
    acceptedAt: '',
    acceptedBy: current.acceptedBy,
    claimIds: nextClaimIds,
    supersedes: current.id,
    note: 'Pending accept preview',
  };
  return diffVersions(current, nextVersion, claims);
}

export function allClaims(revisedBalance = false) {
  return revisedBalance ? claimsForSession(true) : CLAIMS;
}
